import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../utils/AuthContext';
import { getProfile } from '../utils/db/profiles';
import { FileText, Printer, Search } from 'lucide-react';
import { toast } from 'react-toastify';

const CreditorsReport = () => {
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (user) {
            getProfile(user.id).then(data => {
                if (data) setProfile(data);
            }).catch(err => console.error("Error loading profile:", err));
        }
    }, [user]);

    // Filter State
    const [customerInput, setCustomerInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('Both'); // Both, Paid, Unpaid
    const [typeFilter, setTypeFilter] = useState('Both');     // Both, GST, Non-GST

    // Data State
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Initial fetch of filters or just wait for user to click Show?
    // User requested "Show button", so we wait.

    // Autocomplete Logic
    const fetchSuggestions = async (query) => {
        if (!query) {
            setSuggestions([]);
            return;
        }

        try {
            const { data, error } = await supabase
                .from('customers')
                .select('customer_name, customer_id')
                .eq('user_id', user.id)
                .or(`customer_name.ilike.${query}%,customer_id.ilike.${query}%`)
                .limit(5);

            if (error) throw error;
            setSuggestions(data || []);
            setShowSuggestions(true);
        } catch (err) {
            console.error('Error fetching suggestions:', err);
        }
    };

    const handleCustomerChange = (e) => {
        const value = e.target.value;
        setCustomerInput(value);
        if (value.length > 0) {
            fetchSuggestions(value);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const selectSuggestion = (name) => {
        setCustomerInput(name);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleShow = async () => {
        if (!user) return;
        setLoading(true);
        setHasSearched(true);
        try {
            // 1. Fetch Data
            let query = supabase
                .from('invoices')
                .select('grand_total, amount_paid, payment_status, invoice_date, invoice_no, customer_id, customers!inner(customer_name, mobile, customer_id)')
                .eq('user_id', user.id);

            // Apply Date Filters
            if (startDate) query = query.gte('invoice_date', startDate);
            if (endDate) query = query.lte('invoice_date', endDate);

            const { data: invoices, error } = await query;
            if (error) throw error;

            // 2. Process Data (Detailed List)
            const processedList = [];

            invoices.forEach(inv => {
                // Filter by Customer Name/ID Input (Strict Match)
                if (customerInput) {
                    const search = customerInput.toLowerCase().trim();
                    const custName = inv.customers?.customer_name?.toLowerCase() || '';
                    const custId = inv.customers?.customer_id?.toLowerCase() || '';

                    if (custName !== search && custId !== search) {
                        return; // Skip
                    }
                }

                // Determine Invoice Type
                const isNonGst = inv.invoice_no?.toString().startsWith('NG-');
                const invoiceType = isNonGst ? 'Non-GST' : 'GST';

                // Filter by Type
                if (typeFilter === 'GST' && isNonGst) return;
                if (typeFilter === 'Non-GST' && !isNonGst) return;

                // Calculate Balance
                const total = parseFloat(inv.grand_total || 0);
                const paid = parseFloat(inv.amount_paid || 0);
                const balance = total - paid;

                // Filter by Status (Invoice Level)
                const isPaid = balance <= 0.5;
                if (statusFilter === 'Paid' && !isPaid) return;
                if (statusFilter === 'Unpaid' && isPaid) return;

                processedList.push({
                    id: inv.invoice_no, // Unique key
                    invoiceType,
                    invoiceNo: inv.invoice_no,
                    customerName: inv.customers?.customer_name,
                    invoiceDate: inv.invoice_date, // Format later
                    amount: total,
                    paid: paid,
                    balance: balance,
                    mobile: inv.customers?.mobile
                });
            });

            // Sort by Date Descending
            processedList.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));

            setReportData(processedList);

        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (reportData.length === 0) {
            toast.warn("No data to export");
            return;
        }
        window.print();
    };

    // Calculate totals for Summary Cards
    const totalInvoicedSum = reportData.reduce((acc, curr) => acc + curr.amount, 0);
    const totalPaidSum = reportData.reduce((acc, curr) => acc + curr.paid, 0);
    const totalBalanceSum = reportData.reduce((acc, curr) => acc + curr.balance, 0);

    return (
        <>
            <div className="space-y-6 print:hidden">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                            <FileText className="mr-2 h-6 w-6 text-primary-600" />
                            Creditor's Report
                        </h1>
                        <p className="text-gray-600">Detailed invoice and payment report</p>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="bg-white p-4 rounded-lg shadow space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                        <div className="md:col-span-1 relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                            <input
                                type="text"
                                value={customerInput}
                                onChange={handleCustomerChange}
                                onFocus={() => customerInput && setShowSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} // Delay to allow click
                                placeholder="Name..."
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                                autoComplete="off"
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                                    {suggestions.map((suggestion, index) => (
                                        <li
                                            key={index}
                                            onClick={() => selectSuggestion(suggestion.customer_name)}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                        >
                                            {suggestion.customer_name} ({suggestion.customer_id})
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                            >
                                <option value="Both">Both</option>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Type</label>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                            >
                                <option value="Both">Both</option>
                                <option value="GST">GST</option>
                                <option value="Non-GST">Non-GST</option>
                            </select>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={handleShow}
                                disabled={loading || !customerInput || !startDate || !endDate}
                                className="flex-1 flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title={(!customerInput || !startDate || !endDate) ? "Please fill all fields" : ""}
                            >
                                <Search className="h-4 w-4 mr-1" />
                                Show
                            </button>
                            <button
                                onClick={handlePrint}
                                disabled={reportData.length === 0}
                                className="flex-1 flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                                <Printer className="h-4 w-4 mr-1" />
                                Print
                            </button>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                {hasSearched && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <p className="text-sm font-medium text-gray-500">Total Invoiced Amount</p>
                            <p className="mt-2 text-2xl font-semibold text-gray-900">₹{totalInvoicedSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <p className="text-sm font-medium text-gray-500">Total Paid Amount</p>
                            <p className="mt-2 text-2xl font-semibold text-green-600">₹{totalPaidSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <p className="mt-2 text-sm font-medium text-gray-500">Total Balance Due</p>
                            <p className="mt-2 text-2xl font-semibold text-red-600">₹{totalBalanceSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>
                    </div>
                )}

                {/* Results Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inv No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inv Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                                    </tr>
                                ) : reportData.length > 0 ? (
                                    reportData.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.invoiceType === 'GST' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {item.invoiceType}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.invoiceNo}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.customerName}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.invoiceDate).toLocaleDateString('en-GB')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">₹{item.paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-semibold ${item.balance > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                                ₹{item.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                            {hasSearched ? 'No invoices found matching your filters.' : 'Set filters and click Show to generate report.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {/* Footer Row for Totals */}
                            {!loading && reportData.length > 0 && (
                                <tfoot className="bg-gray-50 font-semibold">
                                    <tr>
                                        <td colSpan="5" className="px-6 py-3 text-right text-gray-900">Total:</td>
                                        <td className="px-6 py-3 text-right text-gray-900">₹{totalInvoicedSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        <td className="px-6 py-3 text-right text-green-600">₹{totalPaidSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        <td className="px-6 py-3 text-right text-red-600">₹{totalBalanceSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {/* --- PRINT LAYOUT --- */}
            <div className="hidden print:block bg-white text-black">
                {/* Header / Logo Section */}
                <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
                    <div className="flex-1 space-y-1">
                        <h1 className="text-2xl font-bold uppercase">{profile?.franchise_name || 'COMPANY NAME'}</h1>
                        <p className="text-sm font-medium">{profile?.address || 'Company Address'}</p>
                        <p className="text-sm">Mobile: {profile?.mobile_no || '-'} | Email: {profile?.email || '-'}</p>
                        <p className="text-sm font-semibold">GST No: {profile?.gst_no || '-'}</p>
                    </div>
                    <div className="flex-shrink-0 ml-6">
                        <div className="h-20 flex items-center justify-center">
                            <img
                                src={profile?.logo_url || '/assets/dashboard-logo.png'}
                                alt="Company Logo"
                                className="max-w-full max-h-full object-contain"
                                crossOrigin="anonymous"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    </div>
                </div>

                {/* Title Section */}
                <div className="text-center mb-6">
                    <h2 className="text-xl font-bold underline mb-2">CREDITOR'S REPORT</h2>
                    <div className="flex justify-between text-sm font-medium px-4">
                        <div className="text-left space-y-1">
                            <p><span className="font-semibold text-gray-700">From:</span> {profile?.franchise_name || 'Our Company'}</p>
                            <p><span className="font-semibold text-gray-700">To:</span> {customerInput ? customerInput : 'All Customers'}</p>
                        </div>
                        <div className="text-right space-y-1">
                            <p><span className="font-semibold text-gray-700">Period:</span> {(startDate && endDate) ? `${new Date(startDate).toLocaleDateString('en-GB')} to ${new Date(endDate).toLocaleDateString('en-GB')}` : 'All'}</p>
                            <p><span className="font-semibold text-gray-700">Generated:</span> {new Date().toLocaleDateString('en-GB')}</p>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="mb-6">
                    <table className="w-full border-collapse border border-gray-800 text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-gray-800 px-2 py-2 text-left w-12 text-center text-xs">Sr No</th>
                                <th className="border border-gray-800 px-2 py-2 text-left text-xs">Type</th>
                                <th className="border border-gray-800 px-2 py-2 text-left text-xs">Inv No</th>
                                <th className="border border-gray-800 px-2 py-2 text-left text-xs">Customer Name</th>
                                <th className="border border-gray-800 px-2 py-2 text-left text-xs">Inv Date</th>
                                <th className="border border-gray-800 px-2 py-2 text-right text-xs">Amount</th>
                                <th className="border border-gray-800 px-2 py-2 text-right text-xs">Paid</th>
                                <th className="border border-gray-800 px-2 py-2 text-right text-xs">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((item, index) => (
                                <tr key={item.id}>
                                    <td className="border border-gray-800 px-2 py-1 text-center text-xs">{index + 1}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-xs">{item.invoiceType}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-xs">{item.invoiceNo}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-xs font-semibold">{item.customerName}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-xs">{new Date(item.invoiceDate).toLocaleDateString('en-GB')}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-right text-xs">₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-right text-xs text-gray-700">₹{item.paid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="border border-gray-800 px-2 py-1 text-right text-xs font-bold">₹{item.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            ))}
                        </tbody>
                        {reportData.length > 0 && (
                            <tfoot>
                                <tr className="bg-gray-100 font-bold">
                                    <td colSpan="5" className="border border-gray-800 px-2 py-2 text-right text-xs">GRAND TOTAL:</td>
                                    <td className="border border-gray-800 px-2 py-2 text-right text-xs">₹{totalInvoicedSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="border border-gray-800 px-2 py-2 text-right text-xs">₹{totalPaidSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="border border-gray-800 px-2 py-2 text-right text-xs">₹{totalBalanceSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                    {reportData.length === 0 && (
                        <div className="text-center py-8 text-gray-500 font-medium">No data available to print.</div>
                    )}
                </div>

                {/* Signature Block */}
                <div className="mt-16 flex justify-end">
                    <div className="text-center">
                        <p className="font-bold mb-16">{profile?.franchise_name || 'COMPANY NAME'}</p>
                        <div className="w-48 border-t border-gray-800 mx-auto"></div>
                        <p className="mt-2 text-sm font-medium">Authorized Signatory</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreditorsReport;
